import type { SupabaseClient } from '@supabase/supabase-js';
import { useStore, type Entitlement } from './store';

/**
 * Asks the server for the account's plan (it reconciles Clerk Billing, Clerk
 * metadata and Supabase first) and applies the answer to the store. Used when
 * the app opens and after plan changes made outside our own billing routes
 * (Clerk's checkout and profile dialogs), whose results reach us by webhook.
 */
export const reconcileEntitlement = async (
  getToken: () => Promise<string | null>,
  userId?: string | null,
): Promise<Entitlement | null> => {
  try {
    const token = await getToken();
    if (!token) return null;
    const res = await fetch('/api/billing/reconcile', { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) return null;
    const entitlement = (await res.json()) as Entitlement;
    useStore.getState().applyEntitlement(entitlement, userId);
    return entitlement;
  } catch (err) {
    console.error('[Entitlement] Reconcile failed:', err);
    return null;
  }
};

// A returning tab may have missed a push while its socket slept.
const RETURN_REFRESH_AFTER_MS = 60_000;
// setTimeout can't wait longer than ~24.8 days.
const MAX_TIMER_MS = 2 ** 31 - 1;

/**
 * Keeps the open app's plan current without polling. Plan changes reach
 * `profiles` from the billing webhook (any change made in Clerk, including a
 * cancellation or downgrade taking effect at period end) or from reconcile; a
 * trigger then bumps the user's `entitlement_signals` row, which Realtime pushes
 * here, and the app asks the server for the plan. Backstops: a timer at the end
 * of the paid period when a change is scheduled, and a refresh when the tab comes
 * back after a while or the Realtime channel reconnects. Returns a cleanup.
 */
export const watchEntitlement = (
  supabase: SupabaseClient,
  getToken: () => Promise<string | null>,
  userId: string,
): (() => void) => {
  let lastRefresh = Date.now();
  let inFlight: Promise<unknown> | null = null;
  const refresh = () => {
    lastRefresh = Date.now();
    if (!inFlight) inFlight = reconcileEntitlement(getToken, userId).finally(() => { inFlight = null; });
    return inFlight;
  };

  let joinedOnce = false;
  const channel = supabase
    .channel(`entitlement-${userId}`)
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'entitlement_signals', filter: `user_id=eq.${userId}` },
      () => { refresh(); },
    )
    .subscribe((status) => {
      if (status !== 'SUBSCRIBED') return;
      if (joinedOnce) refresh();
      joinedOnce = true;
    });

  // The paid tier stays until the period ends; Clerk's webhook moves it then, and
  // this catches a tab that is open at that moment if the push is late or lost.
  let periodEndTimer: ReturnType<typeof setTimeout> | undefined;
  const schedulePeriodEnd = () => {
    clearTimeout(periodEndTimer);
    const { cancelAtPeriodEnd, subscriptionEndsAt, trialEndsAt, isProUser } = useStore.getState();
    const endsAt = isProUser ? (cancelAtPeriodEnd ? subscriptionEndsAt : null) || trialEndsAt : null;
    if (!endsAt) return;
    const delay = new Date(endsAt).getTime() - Date.now() + 5_000;
    if (Number.isFinite(delay) && delay > 0 && delay < MAX_TIMER_MS) periodEndTimer = setTimeout(refresh, delay);
  };
  schedulePeriodEnd();
  const unsubscribeStore = useStore.subscribe((state, prev) => {
    if (
      state.cancelAtPeriodEnd !== prev.cancelAtPeriodEnd ||
      state.subscriptionEndsAt !== prev.subscriptionEndsAt ||
      state.trialEndsAt !== prev.trialEndsAt ||
      state.isProUser !== prev.isProUser
    ) schedulePeriodEnd();
  });

  const onVisible = () => {
    if (document.visibilityState === 'visible' && Date.now() - lastRefresh > RETURN_REFRESH_AFTER_MS) refresh();
  };
  document.addEventListener('visibilitychange', onVisible);

  return () => {
    supabase.removeChannel(channel);
    clearTimeout(periodEndTimer);
    unsubscribeStore();
    document.removeEventListener('visibilitychange', onVisible);
  };
};
